import { stringify } from 'querystring';
import dynamic from 'next/dynamic';
import React, { useState } from 'react';
import styled from 'styled-components';
import useSWRImmutable from 'swr/immutable';
import { hover } from '@peloton/styles';
import { media } from '@peloton/styles/breakpoints';
import { track } from '@engage/analytics/actions';
import { BrowseCategorySlug } from '@engage/browse-categories/models';
import type { TopNavUserInfo } from '@engage/members/selectors';
import { useReduxAction } from '@engage/redux/hooks';
import { spaces } from '@engage/styles/spaces';
import { useTopNav, useActivityFeed } from '@members/copy/copy.generated';
import { ActivityFeed } from '@members/copy/models.generated/ActivityFeed';
import { TopNav } from '@members/copy/models.generated/TopNav';
import { StackedClassesFetchersGQL } from '@members/data-fetch';
import { useFeatureToggle } from '@members/feature-toggles/featureToggles';
import { Feature } from '@members/feature-toggles/toggles';
import { Bookmark } from '@members/icons/Bookmark';
import { Calendar } from '@members/icons/Calendar';
import { CalendarAlert } from '@members/icons/CalendarAlert';
import { StackedPlay } from '@members/icons/StackedPlay';
import { StackedPlayAlert } from '@members/icons/StackedPlayAlert';
import { useMembersRouter } from '@members/localization/useMembersRouter';
import {
  bookmarkButtonClassName,
  bookmarkParentClassName,
  scheduleButtonClassName,
  scheduleParentClassName,
  stacksButtonClassName,
  stacksParentClassName,
  searchParentClassName,
  searchButtonClassName,
} from '@members/onboarding-tooltips/classNames';
const FeedNotificationsContainer = dynamic(
  () => import('@members/pg-feed/components/FeedNotificationsContainer'),
  { ssr: false },
);
import SearchIcon from '@members/pg-library/icons/SearchIcon';
import { toLibraryCategoryRoute } from '@members/pg-library/urls';
import { YOUR_SCHEDULE_ROUTE } from '@members/schedule/urls';
import { Source } from '@members/stacks/analytics/events';
import { TooltipTarget } from '@members/tooltips/TooltipTarget';
import { focusLinkStyles, hoverLinkStyles, MainLink } from '../../../sharedLinkStyles';
import {
  ANALYTICS_SOURCE,
  trackTappedHomescreenTopNavigation,
  trackViewedNavItem,
} from '../../analytics';
import { openStackFromNav } from '../StackFromNav';
import { usePendingInviteSetup } from '../TopNavLinks';
import { NotificationsBadge } from './NotificationsBadge';
import MiniNavTooltip from './ToolTip';

const bookmarkMap = { ['is_favorite_ride']: JSON.stringify(['true']) };

type Props = {
  user?: TopNavUserInfo;
};
export const MiniNavIcons: React.FC<React.PropsWithChildren<Props>> = ({
  user,
}: Props) => {
  const router = useMembersRouter();
  const trackAnalytics = useReduxAction(track);
  const handleStackClick = useReduxAction(() => openStackFromNav(Source.Navigation));
  const [isExpanded, setIsExpanded] = useState(false);
  const { valueToShow } = usePendingInviteSetup();
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const bookmarkCopy = useTopNav(TopNav.Bookmarks);
  const stackedPlayCopy = useTopNav(TopNav.StackedClasses);
  const calendarCopy = useTopNav(TopNav.YourSchedule);
  const notificationsCopy = useTopNav(TopNav.Notifications);
  const searchCopy = useActivityFeed(ActivityFeed.Search);
  const { data } = useSWRImmutable(...StackedClassesFetchersGQL.ViewUserStackQuery({}));

  const numStackedClasses = data?.viewUserStack?.numClasses ?? -1;
  const navUpdatesEnabled = useFeatureToggle(Feature.Navupdates);
  const hideFeedNav = useFeatureToggle(Feature.Hide_feed_nav);

  const getId = (term: string): string => {
    return term.toLowerCase().replace(/\s+/g, '-');
  };

  return (
    <MiniNavIconContainer>
      {navUpdatesEnabled ? (
        <>
          <div className={searchParentClassName} data-test-id="navSearchIconContainer">
            <TooltipTarget tooltipId={getId(searchCopy)} isExpanded={isExpanded}>
              <LinkIconContainer
                href={{
                  pathname: toLibraryCategoryRoute(BrowseCategorySlug.All),
                  search: `?${stringify(bookmarkMap)}`,
                  query: { source: ANALYTICS_SOURCE },
                }}
                data-test-id="searchLink"
                onClick={event => {
                  event.preventDefault();
                  router.push(
                    {
                      pathname: '/search?c=all',
                      query: {
                        source: ANALYTICS_SOURCE,
                        from: router.asPath,
                      },
                    },
                    '/search?c=all',
                  );
                }}
                aria-label={searchCopy}
                className={searchButtonClassName}
              >
                <SearchIcon />
              </LinkIconContainer>
            </TooltipTarget>
          </div>
          <MiniNavTooltip
            tooltipId={getId(searchCopy)}
            tooltipLabel={searchCopy}
            setIsExpanded={setIsExpanded}
          ></MiniNavTooltip>
        </>
      ) : null}
      <div className={bookmarkParentClassName} data-test-id="navBookmarkIconContainer">
        <TooltipTarget tooltipId={getId(bookmarkCopy)} isExpanded={isExpanded}>
          <LinkIconContainer
            href={{
              pathname: toLibraryCategoryRoute(BrowseCategorySlug.All),
              search: `?${stringify(bookmarkMap)}`,
              query: { source: ANALYTICS_SOURCE },
            }}
            data-test-id="bookmarkLink"
            onClick={event => {
              event.preventDefault();

              trackAnalytics(trackViewedNavItem('Bookmarks'));
              trackAnalytics(
                trackTappedHomescreenTopNavigation('Bookmarks', 'Navigation'),
              );

              router.push(
                {
                  pathname: toLibraryCategoryRoute(BrowseCategorySlug.All),
                  query: {
                    ...bookmarkMap,
                    source: ANALYTICS_SOURCE,
                  },
                },
                toLibraryCategoryRoute(BrowseCategorySlug.All) +
                  `?${stringify(bookmarkMap)}`,
              );
            }}
            aria-label={bookmarkCopy}
            className={bookmarkButtonClassName}
          >
            <Bookmark />
          </LinkIconContainer>
        </TooltipTarget>
      </div>
      <MiniNavTooltip
        tooltipId={getId(bookmarkCopy)}
        tooltipLabel={bookmarkCopy}
        setIsExpanded={setIsExpanded}
      ></MiniNavTooltip>
      <div className={stacksParentClassName} data-test-id="navStacksIconContainer">
        <TooltipTarget tooltipId={getId(stackedPlayCopy)} isExpanded={isExpanded}>
          <ButtonIconContainer
            data-test-id="stacksLink"
            onClick={() => {
              trackAnalytics(trackTappedHomescreenTopNavigation('Stacks', 'Navigation'));
              handleStackClick();
            }}
            aria-label={stackedPlayCopy}
            className={stacksButtonClassName}
          >
            {numStackedClasses > 0 ? <StackedPlayAlert /> : <StackedPlay />}
          </ButtonIconContainer>
        </TooltipTarget>
      </div>
      <MiniNavTooltip
        tooltipId={getId(stackedPlayCopy)}
        tooltipLabel={stackedPlayCopy}
        setIsExpanded={setIsExpanded}
      ></MiniNavTooltip>
      <div className={scheduleParentClassName} data-test-id="navScheduleIconContainer">
        <TooltipTarget tooltipId={getId(calendarCopy)} isExpanded={isExpanded}>
          <LinkIconContainer
            href={{ pathname: YOUR_SCHEDULE_ROUTE, query: { source: ANALYTICS_SOURCE } }}
            alias={YOUR_SCHEDULE_ROUTE}
            data-test-id="yourScheduleLink"
            onClick={event => {
              event.preventDefault();
              trackAnalytics(
                trackTappedHomescreenTopNavigation('YourSchedule', 'Navigation'),
              );
              router.push(
                {
                  pathname: YOUR_SCHEDULE_ROUTE,
                  query: {
                    source: ANALYTICS_SOURCE,
                  },
                },
                YOUR_SCHEDULE_ROUTE,
              );
            }}
            aria-label={calendarCopy}
            className={scheduleButtonClassName}
          >
            {valueToShow > 0 ? <CalendarAlert /> : <Calendar />}
          </LinkIconContainer>
        </TooltipTarget>
      </div>
      <MiniNavTooltip
        tooltipId={getId(calendarCopy)}
        tooltipLabel={calendarCopy}
        setIsExpanded={setIsExpanded}
      ></MiniNavTooltip>
      {!hideFeedNav && (
        <>
          <TooltipTarget tooltipId={getId(notificationsCopy)} isExpanded={isExpanded}>
            <ButtonIconContainer
              data-test-id="notificationsLink"
              onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
              aria-label={notificationsCopy}
              aria-expanded={isNotificationsOpen}
              aria-controls="Notifications"
            >
              <NotificationsBadge isNotificationsOpen={isNotificationsOpen} />
            </ButtonIconContainer>
          </TooltipTarget>
          <MiniNavTooltip
            tooltipId={getId(notificationsCopy)}
            tooltipLabel={notificationsCopy}
            setIsExpanded={setIsExpanded}
          ></MiniNavTooltip>
          {isNotificationsOpen && (
            <FeedNotificationsContainer
              isOpen={isNotificationsOpen}
              setIsOpen={setIsNotificationsOpen}
            />
          )}
        </>
      )}
    </MiniNavIconContainer>
  );
};

const MiniNavIconContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 56px;
`;

const ButtonIconContainer = styled.button`
  cursor: pointer;
  align-items: center;
  display: flex;
  padding: ${spaces.small}px;
  ${hover`
    transition: background-color 250ms ease;
    border-radius: 9999px;
  `}
  ${media.tablet`
    ${hoverLinkStyles}
    ${focusLinkStyles}
  `}
`;

const LinkIconContainer = styled(MainLink)`
  cursor: pointer;
  align-items: center;
  display: flex;
  padding: ${spaces.small}px;
  ${hover`
    transition: background-color 250ms ease;
    border-radius: 9999px;
  `}
  ${media.tablet`
    ${hoverLinkStyles}
    `}
`;
